import React, {ChangeEvent, Component, ReactNode} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {NUMBER_OF_TIMES_TO_STAY} from '../../../CodeMaster'
import {numberOrNull} from '../../../stringHelper/stringHelpers'
import {OvernightFacilitySetting} from '../../facilities/FacilityForReservation'
import {ReserveFacilityModel} from '../ReserveFacilityModel'
import DatePickerForResident from '../components/DatePickerForResident'
import {DisplayItemInputTitleRequiredNoMargin} from '../../../styledComponents/DisplayItem'
import DisplayItemSelectComponent from '../../../styledComponents/DisplayItemSelectComponent'
import {
    BlockSpacerSpOnly,
    GridBlockSpacer,
    GridContainer,
    GridItem,
} from '../../../styledComponents/ResponsiveConvenience'
import {StyledNote} from '../components/CommonStyle'
import moment from 'moment'
import {addEmptyData} from '../../../componentHelper/selectHelper'
import styled from 'styled-components'
import {Colors} from '../../../styledComponents/Constants'

interface Props {
    facilityData: OvernightFacilitySetting
    reservationModel: ReserveFacilityModel

    defaultStartDate: string
    defaultStayCount: Nullable<number>

    onChange: (startDate: string, stayCount: number) => void
    hasError?: boolean
    currentDateTime: moment.Moment
    holidays: string[]
}

interface State {
    startDate: string
    stayCount: number
}

class RRFFReserveDataInputOvernightFacility extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleChangeDate = this.handleChangeDate.bind(this)
        this.handleChangeStayCount = this.handleChangeStayCount.bind(this)

        this.state = {
            startDate: props.defaultStartDate,
            stayCount: props.defaultStayCount || 1,
        }
    }

    componentDidMount(): void {
        const {defaultStartDate, facilityData} = this.props
        this.setState({startDate: defaultStartDate}, () => {
            if (!isValidStayCount(this.state.stayCount, facilityData.stayPossibleDateCount)) {
                return
            }
            this.props.reservationModel.withOvernightReservationData(
                this.state.startDate,
                this.state.stayCount,
            )
        })
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        const {defaultStartDate, facilityData} = this.props
        if (prevProps.defaultStartDate === defaultStartDate) {
            return
        }
        this.setState({startDate: defaultStartDate}, () => {
            if (!isValidStayCount(this.state.stayCount, facilityData.stayPossibleDateCount)) {
                return
            }
            this.props.reservationModel.withOvernightReservationData(
                this.state.startDate,
                this.state.stayCount,
            )
        })
    }

    handleChangeDate(date: string): void {
        this.setState({
            startDate: date,
        }, () => {
            this.notifyChanged()
        })
    }

    handleChangeStayCount(event: ChangeEvent<HTMLSelectElement>): void {
        const stayCount = numberOrNull(event.target.value) || 1
        this.setState({
            stayCount: stayCount,
        }, () => {
            this.notifyChanged()
        })
    }

    notifyChanged(): void {
        this.props.onChange(
            this.state.startDate,
            this.state.stayCount,
        )
        if (!isValidStayCount(this.state.stayCount, this.props.facilityData.stayPossibleDateCount)) {
            return
        }
        this.props.reservationModel.withOvernightReservationData(
            this.state.startDate,
            this.state.stayCount,
        )
    }

    render(): ReactNode {
        const {checkinTime, checkoutTime, stayPossibleDateCount} = this.props.facilityData
        const {startDate, stayCount} = this.state
        const {hasError, holidays} = this.props
        const zeroSuppress = (time: string): string => moment(time, 'HH:mm').format('H:mm')
        const checkinString = zeroSuppress(checkinTime)
        const checkoutString = zeroSuppress(checkoutTime)
        const stayCountError = !isValidStayCount(stayCount, stayPossibleDateCount)
        const defaultStayCountItems = NUMBER_OF_TIMES_TO_STAY.values()
            .filter(item => Number(item.value) <= stayPossibleDateCount)
            .map(item => {
                return {value: item.value, label: `${item.label}泊`}
            })
        const stayCountItems = stayCountError
            ? addEmptyData(defaultStayCountItems, '選択してください')
            : defaultStayCountItems

        return (<GridContainer>
            <GridItem over={3}>
                <DisplayItemInputTitleRequiredNoMargin>
                    宿泊開始日
                </DisplayItemInputTitleRequiredNoMargin>
            </GridItem>
            <GridItem over={9}>
                <BlockSpacerSpOnly height={'10px'}/>
                <DatePickerForResident
                    date={startDate}
                    selectableStartDate={this.props.currentDateTime}
                    hasError={hasError}
                    onChange={this.handleChangeDate}
                    holidays={holidays}
                />
            </GridItem>

            <GridItem over={3}/>
            <GridItem over={9}>
                <StyledNote>
                    {`使用可能時間：${checkinString} から 翌日${checkoutString} まで`}
                </StyledNote>
            </GridItem>

            <GridBlockSpacer height={'20px'}/>

            <GridItem over={3}>
                <DisplayItemInputTitleRequiredNoMargin>
                    宿泊数
                </DisplayItemInputTitleRequiredNoMargin>
            </GridItem>
            <GridItem over={9}>
                <BlockSpacerSpOnly height={'10px'}/>
                <DisplayItemSelectComponent
                    hasError={hasError || stayCountError}
                    value={stayCount}
                    onChange={this.handleChangeStayCount}>
                    {
                        stayCountItems
                            .map((item, index) => {
                                return <option key={index} value={item.value}>{item.label}</option>
                            })
                    }
                </DisplayItemSelectComponent>
            </GridItem>
            {
                stayCountError &&
                <>
                    <GridItem over={3}/>
                    <GridItem over={9}>
                        <StyledNoteError>
                            {`1度に予約できるのは${stayPossibleDateCount}泊までです。`}
                        </StyledNoteError>
                    </GridItem>
                </>
            }
        </GridContainer>)
    }
}

export default RRFFReserveDataInputOvernightFacility

const isValidStayCount = (count: number, possibleCount: number): boolean => {
    return (1 <= count && count <= possibleCount)
}

const StyledNoteError = styled(StyledNote)`
  color: ${Colors.Alert};
`
